import { useCallback } from "react";
import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface SectionLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  /** Target homepage section id, without the leading "#" — e.g. "featured", "host", "top". */
  section: string;
  children: ReactNode;
}

/** Smooth-scroll the document to the element with this id, if it exists. */
export function scrollToSection(section: string): void {
  if (section === "top") {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  document.getElementById(section)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * Reliable navigation to a homepage section (`#featured`, `#host`, …) from anywhere on the site.
 *
 * Native `<a href="#section">` anchors are unreliable in this SPA: from a sub-page they resolve to
 * `/#section` and trigger a full-page reload that lands at the top (the target section hasn't
 * painted when the browser tries to honor the hash). Instead this component:
 *  - on the homepage: smooth-scrolls to the section in place and syncs the URL hash — no reload;
 *  - on any other route: client-navigates to "/" carrying the hash, and <HomeScreen> scrolls to it
 *    once mounted (see useScrollToHash).
 *
 * The rendered `href` still points at the resolved target so middle-click / "open in new tab" and
 * crawlers get a real URL.
 */
export function SectionLink({ section, children, onClick, ...rest }: SectionLinkProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const hash = `#${section}`;
  const onHome = location.pathname === "/";
  const href = onHome ? hash : `/${hash}`;

  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(e);
      if (e.defaultPrevented) return;
      // Let modified clicks (new tab, middle-click) behave natively.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      if (onHome) {
        scrollToSection(section);
        // Sync the address bar without a navigation so deep-links/back still work.
        window.history.replaceState(window.history.state, "", hash);
      } else {
        navigate({ pathname: "/", hash });
      }
    },
    [hash, navigate, onClick, onHome, section],
  );

  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
